import { useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetApi, useIsOnline, useTheme } from "src/hooks";
import useSavedCursorPosition from "src/hooks/useSavedCursorPosition";
import { useSelector } from "src/redux/hooks";
import { getYachtId } from "src/utils";

import { useInputProps } from "../hooks/useInputProps";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";

interface Props {
  id: string;
  value: string;
  required?: boolean;
  searchEntries?: string;
  isOverlay?: boolean;
  textarea?: boolean; // Toggle between input and textarea
  minRows?: number; // Minimum number of rows for textarea
  maxRows?: number; // Optional maximum number of rows before scrolling
}

const mapper = (data: any) => {
  if (!data) return [];
  return [...new Set(data.list)];
};
// do not change if it works it works!
const TextAutocomplete = ({
  id,
  value,
  required = false,
  searchEntries,
  textarea = false,
  minRows = 1,
  maxRows = 10,
}: Props) => {
  const theme = useTheme();
  const isOnline = useIsOnline();
  const { uppercase } = useSelector(state => state.preferencesSlice);
  const { logbookId } = useParams();
  const inputRef = useRef<any>(null);
  const shadowTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { onChange, ...inputProps } = useInputProps(id, value, required);
  const [suggestion, setSuggestion] = useState("");
  const [cursorPosition, setCursorPositionState] = useState<number>(0);
  const [isFocused, setIsFocused] = useState(false);

  // Get the centralized tab navigation props
  const { ref: addToRefs, onKeyDown: handleLogbookKeyDown } =
    useLogbookRefsContext(id);

  // Find the current line based on cursor position for textarea mode
  const getCurrentLine = () => {
    if (!textarea || !inputProps.value) return "";

    const lines = inputProps.value.split("\n");
    let charCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const lineLength = lines[i].length + (i < lines.length - 1 ? 1 : 0); // +1 for newline char
      if (charCount + lineLength >= cursorPosition) {
        // Return line text up to cursor position
        const posInLine = cursorPosition - charCount;
        return lines[i].substring(0, posInLine);
      }
      charCount += lineLength;
    }

    return "";
  };

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    onChange(val);

    // Get and store cursor position
    const position = e.target.selectionStart || 0;
    setCursorPositionState(position);
    setCursorPosition();

    if (!isOnline) return;

    // For textareas, use current line for autocompletion; for inputs, use full value
    const searchText = textarea ? getCurrentLine() : val;

    // No suggestions for empty search text
    if (!searchText) {
      setSuggestion("");
      return;
    }

    const data = await refetch(
      {
        params: {
          text: searchText,
          searchID: searchEntries,
          lbid: logbookId,
          yachtid: getYachtId(),
        },
      },
      true,
    );

    if (!data || !Array.isArray(data) || data.length === 0) {
      setSuggestion("");
      return;
    }

    // Find first matching prediction
    const match = data.find((word: string) =>
      word.toLowerCase().startsWith(searchText.toLowerCase()),
    );

    if (match && match.toLowerCase() !== searchText.toLowerCase()) {
      if (textarea) {
        // For textarea, we need to combine the suggested completion with the full text
        const lines = val.split("\n");
        let charCount = 0;
        let suggestionText = val;

        for (let i = 0; i < lines.length; i++) {
          const lineLength = lines[i].length + (i < lines.length - 1 ? 1 : 0);
          if (charCount + lineLength >= cursorPosition) {
            // Found the line with cursor

            // Create suggestion by replacing current line part with match
            const newLine = match;
            lines[i] = newLine;
            suggestionText = lines.join("\n");
            break;
          }
          charCount += lineLength;
        }
        setSuggestion(suggestionText);
      } else {
        // Standard input behavior - just use the match directly
        setSuggestion(match);
      }
    } else {
      setSuggestion("");
    }
  };
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Handle autocomplete suggestion with Tab
    if (e.key === "Tab" && suggestion) {
      e.preventDefault();
      onChange(suggestion);
      setSuggestion("");
      return; // Don't let the centralized system handle this Tab press
    }

    // Let the centralized tab navigation system handle Tab when no suggestion
    if (e.key === "Tab") {
      handleLogbookKeyDown(e);
      return;
    }

    // For textareas, we need to update cursor position on navigation
    if (
      textarea &&
      [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    ) {
      // Use setTimeout to let the default key behavior complete
      setTimeout(() => {
        if (inputRef.current) {
          setCursorPositionState(inputRef.current.selectionStart || 0);
        }
      }, 0);
    }
  };

  const { refetch } = useGetApi({
    request: "search-entry-values",
    body: {
      params: {
        text: "", // Will be set dynamically in handleChange
        searchID: searchEntries,
        lbid: logbookId,
        yachtid: getYachtId(),
      },
    },
    fetchOnInit: false,
    mapper,
  });
  const { handleFocus, handleBlur, setCursorPosition } = useSavedCursorPosition(
    id,
    inputRef,
  );

  const handleInputFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setIsFocused(true);
    handleFocus(e);
  };

  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setIsFocused(false);
    handleBlur(e);
  };

  // Auto-grow textarea height to fit content
  const [autoHeight, setAutoHeight] = useState<string>("36px");
  useLayoutEffect(() => {
    if (textarea && inputRef.current) {
      const textareaElem = inputRef.current as HTMLTextAreaElement;
      textareaElem.style.height = "auto";
      const lineHeight =
        parseInt(window.getComputedStyle(textareaElem).lineHeight, 10) || 20;
      const maxHeight = lineHeight * maxRows;
      const scrollHeight = textareaElem.scrollHeight;
      const newHeight = Math.min(scrollHeight, maxHeight);
      textareaElem.style.height = newHeight + "px";
      setAutoHeight(newHeight + "px");
    }
  }, [inputProps.value, textarea, maxRows]);
  // Shared styles for both input and textarea
  const sharedStyles = {
    background: "transparent",
    width: "100%",
    border: inputProps.error
      ? `1px solid ${theme?.colors?.red?.[6] ?? "#fa5252"}`
      : isFocused
        ? `1px solid ${theme?.colors?.main?.[5]}` // Mantine blue focus color
        : `1px solid ${theme?.colors?.inputBorder?.[0]}`,
    borderRadius: 4,
    padding: textarea ? "8px 10px" : "0 10px", // Reduced padding for textarea
    textTransform: uppercase ? "uppercase" : "none",
    resize: "none" as const,
    overflow: textarea ? ("hidden" as const) : ("visible" as const),
    boxSizing: "border-box" as const,
    minHeight: "36px",
    height: textarea ? autoHeight : "36px",
    display: "flex",
    alignItems: "center",
    outline: "none",
    transition: "border-color 0.2s ease", // Smooth transition
  };

  // Styles specific to the input/textarea element
  const elementStyles = {
    ...sharedStyles,
    position: "absolute" as const,
    caretColor: "black",
    overflowY: "auto",
    zIndex: 1,
  };

  // Styles specific to the suggestion element
  const suggestionStyles = {
    ...sharedStyles,
    color: "gray",
    position: "absolute" as const,
    pointerEvents: "none" as const,
    zIndex: 0,
  };

  // Container style adjusts based on textarea or input
  const containerStyle = {
    position: "relative" as const,
    width: "100%",
    minHeight: "36px",
    // height is now handled by autoHeight for textarea, otherwise 36px for input
    height: textarea ? autoHeight : "36px",
    display: "flex",
    alignItems: "center",
    backgroundColor: theme?.colors?.inputBackgroud?.[0] ?? "",
  };
  // Render either textarea or input based on the textarea prop
  const renderInputElement = () => {
    if (textarea) {
      return (
        <textarea
          ref={ref => {
            inputRef.current = ref;
            addToRefs(ref);
          }}
          value={inputProps.value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          style={elementStyles as any}
          onSelect={e => {
            if (e.currentTarget) {
              setCursorPositionState(e.currentTarget.selectionStart || 0);
            }
          }}
          rows={minRows}
        />
      );
    }

    return (
      <input
        type="text"
        ref={ref => {
          inputRef.current = ref;
          addToRefs(ref);
        }}
        name={id}
        value={inputProps.value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        style={elementStyles as any}
      />
    );
  };

  // Render either textarea or input for suggestion
  const renderSuggestionElement = () => {
    if (textarea) {
      return (
        <textarea
          ref={shadowTextareaRef}
          value={suggestion}
          disabled
          style={suggestionStyles as any}
          rows={minRows}
        />
      );
    }

    return (
      <input
        type="text"
        value={suggestion}
        disabled
        style={suggestionStyles as any}
      />
    );
  };

  return (
    <div style={containerStyle}>
      {renderInputElement()}
      {renderSuggestionElement()}
    </div>
  );
};

export default TextAutocomplete;
